import React, { PureComponent } from 'react';
import TextField from '../text-field';
import DobField from '../dob-field';
import SsnField from '../ssn-field';
import Address from '../address';
import dayJs from 'dayjs';
import {
  validateStreet,
  validateAptSuite,
  validateCity,
  validateState,
  validateZip,
  DEFAULT_STATE
} from '../address/validations';
import { handleOnPasteForNameField } from '../../lib/valid-key';

class Owner extends PureComponent {
  constructor(props) {
    super(props);
    const { index, update } = props;
    this.addressActions = {
      enterStreetAddress: (value) => {
        const errMsg = validateStreet(value);
        const { errors = {} } = this.props.owner;
        errors.street = errMsg ? errMsg : null;
        update(index, 'address.street', value, errors);
      },
      enterAptSuite: (value) => {
        const errMsg = validateAptSuite(value);
        const { errors = {} } = this.props.owner;
        errors.aptsuite = errMsg ? errMsg : null;
        update(index, 'address.aptsuite', value ? value : '', errors);
      },
      enterCity: (value) => {
        const errMsg = validateCity(value);
        const { errors = {} } = this.props.owner;
        errors.city = errMsg;
        update(index, 'address.city', value ? value : '', errors);
      },
      selectState: (value) => {
        const errMsg = validateState(value);
        const { errors = {} } = this.props.owner;
        errors.state = errMsg ? errMsg : null;
        update(index, 'address.state', value ? value : DEFAULT_STATE, errors);
      },
      enterZip: (value) => {
        const errMsg = validateZip(value, 5);
        const { errors = {} } = this.props.owner;
        errors.zip = errMsg ? errMsg : null;
        update(index, 'address.zip', value ? value : '', errors);
      }
    };
  }

  state = {
    errors: {...this.props.owner.errors} || {}
  }

  componentDidUpdate(prevProps) {
    const { owner, index, update, currentTotalShares } = this.props;
    if (owner.triggerValidation && owner.triggerValidation !== prevProps.owner.triggerValidation) {
      const errors = {};
      errors.firstName = !owner.firstName && 'First name is required.';
      errors.lastName = !owner.lastName && 'Last name is required.';

      const { share = 0 } = owner;
      const previousShare = prevProps.owner.share;

      errors.share = previousShare !== share
        ? (share === 0 || (currentTotalShares - previousShare) + share > 100) && 'Total ownership share must be between 1% and 100%.'
        : null;

      errors.dob = (!owner.dob || !dayJs(owner.dob, 'MM/DD/YYYY').isValid()) && 'Please enter a valid date.';
      errors.ssn = (!owner.ssn || owner.ssn.replace(/(\-|\s)/g, '').length !== 9) && 'Please enter a valid ssn.';

      const { address = {} } = owner;
      errors.street = validateStreet(address.street);
      errors.aptsuite = validateAptSuite(address.aptsuite);
      errors.city = validateCity(address.city);
      errors.zip = validateZip(address.zip);
      errors.state = validateState(address.state);

      return update(index, null, null, errors);
    }
  }

  validateRequiredField = (field) => {
    return (e) => {
      const { owner, update, index } = this.props;
      const errors = owner.errors || {};
      const { name, displayName } = field;
      const value = field.value || (e && e.target && e.target.value);

      errors[name] = value
        ? null
        : `${displayName || name } is required.`;

      return update(index, name, value, errors);
    };
  }

  setTextField = (field) => {
    let timeout = null;
    return (e) => {
      const { value } = e.target;

      const { owner, update, index } = this.props;
      const errors = owner.errors || {};
      const { name, displayName } = field;

      if (errors[name]) {
        errors[name] = !value && `${displayName || name } is required.`;
      }

      if (timeout) {
        clearTimeout(timeout);
      }

      timeout = setTimeout(() => {
        update(index, name, value, errors);
      }, 1000);
    };
  }

  validateOwnershipShare = (e) => {
    const { value } = e.target;
    const { owner, update, index } = this.props;
    const errors = { ...owner.errors };
    errors.share = '';
    const share = value ? parseInt(value, 10) : 0;
    if (value) {
      const { share: currentShare } = owner;
      const currentTotalShares =
        this.props.currentTotalShares - (currentShare || 0)
        + share;
      if (share === 0 || (currentTotalShares === 0 || currentTotalShares > 100)) {
        errors.share = 'Total ownership share must be between 1% and 100%.';
        return update(index, 'share', value, errors);
      }
    }
    this.validateRequiredField({ name: 'share', displayName: 'Share of ownership', value: share })(e);
  }

  changeDob = (e) => {
    const { errors = {} } = this.props.owner;
    const { target: { value } } = e;
    const { update, index } = this.props;

    if (errors.dob) {
      errors.dob = !dayJs(value, 'MM/DD/YYYY').isValid() && 'Please enter a valid date.';
    }

    update(index, 'dob', value, errors);
  }

  validateDob = (e, value, isValid) => {
    if (value) {
      const { errors = {} } = this.props.owner;
      errors.dob = '';

      if (!isValid || !dayJs(value, 'MM/DD/YYYY').isValid()) {
        errors.dob = 'Please enter a valid date.';
      } else {
        const dateParts = /(\d{2})\/(\d{2})\/(\d{4})/.exec(value);
        const [, month, day, year ] = dateParts;
        const dateInput = dayJs()
          .set('month', parseInt(month, 10) - 1)
          .set('year', year)
          .set('day', day);

        const date18Plus = dayJs().subtract(18, 'year');

        if (dateInput.isAfter(date18Plus)) {
          errors.dob = 'Individual must be 18 years or older.';
        }

        const date119Older = dayJs().subtract(119, 'year');
        if (dateInput.isBefore(date119Older)) {
          errors.dob = 'Invalid date.';
        }
      }

      const { index, update } = this.props;
      return update(index, 'dob', value, errors);
    }
    const requiredCheck = this.validateRequiredField({ name: 'dob', displayName: 'Date of birth', value })(e);
    return requiredCheck;
  }

  validateSsn = (value, executeValidation = false) => {
    const errors = {...this.props.owner.errors};
    errors.ssn = '';
    if (value) {
      const validSsn = /\d{9}/;
      if (executeValidation && !validSsn.test(value)) {
        errors.ssn = 'SSN must be nine digits.';
      }
      const { index, update } = this.props;
      return update(index, 'ssn', value, errors);
    }

    const validate = this.validateRequiredField({ name: 'ssn', displayName: 'SSN', value });
    validate();
  }

  preventAlphaCharacters = (e) => {
    const { value } = e.target;
    if (/\D/.test(value)) {
      e.target.value = '';
    }
  }

  render() {
    const {
      index,
      owner,
      isController,
      smartystreetsconfig
    } = this.props;

    const errors = owner.errors || {};
    const ssnProps = owner.ssn && owner.ssn.length > 4
      ? { defaultValue: owner.ssn }
      : {};

    const fieldPrefix = isController
      ? 'controller'
      : 'owner';

    return <div>
      <TextField
        data-test-id="first-name-field"
        aria-required
        className='input-helper'
        htmlFor={`${fieldPrefix}_first_name_${index}`}
        labelText='First Name'
        labelClassName='section-label'
        defaultValue={owner.firstName}
        onBlur={this.validateRequiredField({ name: 'firstName', displayName: 'First name' })}
        onInput={this.setTextField({ name: 'firstName', displayName: 'First name' })}
        onPaste={handleOnPasteForNameField}
        errorMsg={errors.firstName}
      />

      <TextField
        data-test-id="last-name-field"
        aria-required
        className='input-helper'
        htmlFor={`${fieldPrefix}_last_name_${index}`}
        labelText='Last Name'
        labelClassName='section-label'
        defaultValue={owner.lastName}
        onBlur={this.validateRequiredField({ name: 'lastName', displayName: 'Last name' })}
        onInput={this.setTextField({ name: 'lastName', displayName: 'Last name' })}
        onPaste={handleOnPasteForNameField}
        errorMsg={errors.lastName}
      />

      {
        !isController &&
        <TextField
          data-test-id="ownership-share-field"
          aria-required
          className='input-helper'
          placeholder='%'
          inputClassName='ownership-share'
          htmlFor={`${fieldPrefix}_share_${index}`}
          defaultValue={owner.share}
          labelText='Ownership Share'
          labelClassName='section-label'
          maxLength='3'
          onBlur={this.validateOwnershipShare}
          onChange={this.validateOwnershipShare}
          onInput={this.preventAlphaCharacters}
          errorMsg={errors.share}
        />
      }

      <DobField
        aria-required
        className='input-helper'
        inputClassName='owner-dob'
        placeholder='MM/DD/YYYY'
        autocomplete='off'
        htmlFor={`${fieldPrefix}_dob_${index}`}
        defaultValue={owner.dob}
        labelText='Date of Birth'
        labelClassName='section-label'
        onChange={this.changeDob}
        onBlur={this.validateDob}
        errorMsg={errors.dob}
      />

      <SsnField
        {...ssnProps}
        aria-required
        className='input-helper'
        inputClassName='owner-ssn'
        autocomplete='off'
        htmlFor={`${fieldPrefix}_ssn_${index}`}
        labelText='Social Security Number (SSN)'
        labelClassName='section-label'
        updateSsn={this.validateSsn}
        errorMsg={errors.ssn}
        helperTextClassName='section-helper-txt'
        helperText='SSN is for verification purposes only. It will not be used to obtain any credit report.'
      />

      <Address
        errors={errors}
        entity={owner.address}
        actions={this.addressActions}
        smartystreetsconfig={smartystreetsconfig}
        labelText='Residential Address'
        helperTextClassName='section-helper-txt'
        helperText='The owners personal, residential address. This is also only used for verification purposes only and we wont send anything to this address.'
        index={index}
      />
    </div>;
  }
}

Owner.defaultProps = {
  index: 1,
  isController: false
};

class OwnerList extends PureComponent {
  getShares = () => {
    return this.props
      .owners
      .reduce((sum, { share = 0 }) => {
        return sum + parseInt(share, 10);
      }, 0);
  }

  updateOwner = (index, field, value, errors) => {
    const { actions } = this.props;
    actions.updateOwner(index, field, value, errors);
  }

  addOwner = (e) => {
    e && e.preventDefault();
    const { actions } = this.props;
    actions.addOwner();
  }

  removeOwner = (e) => {
    e && e.preventDefault();
    const { actions } = this.props;
    actions.removeOwner();
  }

  render() {
    const { owners, smartystreetsconfig } = this.props;
    const currentTotalShares = this.getShares();

    return <div>
      { owners.map((owner, index) =>
        (<div key={`owner_${index}`} className='beneficial-owner'>
          <h2 className='sub-title'>Owner #{index + 1}</h2>
          <Owner
            key={`owner_${index}_bo`}
            update={this.updateOwner}
            owners={owners}
            currentTotalShares={currentTotalShares}
            owner={owner}
            smartystreetsconfig={smartystreetsconfig}
            index={index}
          />
        </div>))
      }
      { owners.length < 4 && <a data-test-id="add-owner-button" className='add-owner-link' onClick={this.addOwner}><span className='add-owner' />Add Another owner</a> }
      { owners.length > 1 && <a data-test-id="remove-owner-button" className='remove-owner-link' onClick={this.removeOwner}><span className='remove-owner' />Remove owner</a> }
    </div>;
  }
}

OwnerList.defaultProps = {
  owners: [{ address: {} }]
};

export default OwnerList;
export {
  Owner
};

